/**
 * GA 의 `item_name` 을 **표시명 하나로** 통일한다 (#5292 C).
 *
 * ## 왜
 *
 * 같은 제품이 슬러그와 표시명으로 갈려 집계된다 — `tactsuit-pro` vs `TactSuit Pro`,
 * `tactsuit-air` vs `Tactsuit Air Ash`. 품목명이 실제 SKU 수보다 훨씬 많은 값으로 쪼개져
 * **품목별 매출·부착률이 실제보다 분산**된다.
 *
 * 가장 큰 오염원은 `cartItemsToAnalyticsItems` 였다 — `item_name` 에 **항상 슬러그**를
 * 넣었다. `begin_checkout` 이 그 경로다.
 *
 * ## 규칙
 *
 * | 입력 | 결과 |
 * | --- | --- |
 * | 표시명 있음 | 그대로 (앞뒤 공백·연속 공백만 정리) |
 * | 표시명 없음 | 슬러그를 **사람이 읽는 형태로** — `tactsuit-pro` → `Tactsuit Pro` |
 * | 깨진 문자 포함 | 그 자리를 지우고 남은 이름 사용 |
 *
 * ⚠️ **`item_id` 는 슬러그를 유지한다.** 이슈가 명시한 대로 식별자는 안정적인 슬러그가
 * 맞고, 통일 대상은 표시용 이름뿐이다. 둘을 같이 바꾸면 기존 GA 리포트의 조인이 깨진다.
 *
 * ⚠️ 슬러그 폴백은 **정확한 제품명이 아니다**(`tactglove-dk3` → `Tactglove Dk3`).
 * 표시명을 넘길 수 있으면 넘길 것 — 이건 "슬러그가 그대로 나가는 것보다 낫다" 수준의
 * 최후 수단이고, 이 값이 GA 에 많이 보이면 이름을 안 넘기는 호출처가 있다는 신호다.
 */

/**
 * 깨진 인코딩 흔적. 실제로 `TactSuit Air ? ash` 같은 값이 관측됐다 — 치환 문자(U+FFFD)나
 * `?` 로 떨어진 자리다. 이름 중간에 남으면 그 자체가 별도 품목으로 집계된다.
 */
const BROKEN_CHARS = /[�]|(?<=\s)\?(?=\s)/g;

/** 슬러그를 표시명 비슷하게 — 폴백 전용. */
const humanizeSlug = (slug: string): string =>
  slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export function normalizeItemName(
  name: string | null | undefined,
  slug: string,
): string {
  const cleaned = (name ?? "")
    .replace(BROKEN_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (cleaned) return cleaned;
  return humanizeSlug(slug);
}
